import React, { useState } from 'react';
import { X, DollarSign, User, Phone, Mail, CreditCard, FileText } from 'lucide-react';
import { salesAPI } from '../services/api';

const SellAssetModal = ({ asset, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    buyer_name: '',
    buyer_phone: '',
    buyer_email: '',
    buyer_id_number: '',
    buyer_address: '',
    sale_price: asset?.current_value || 0,
    payment_method: 'cash',
    payment_status: 'paid',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  
  // Calculate profit
  const profit = formData.sale_price - (asset?.purchase_price || 0);
  const profitMargin = asset?.purchase_price > 0 
    ? ((profit / asset.purchase_price) * 100).toFixed(1)
    : 0;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.buyer_name || !formData.buyer_phone || !formData.sale_price) {
      alert('Nama pembeli, No. HP, dan Harga Jual wajib diisi!');
      return;
    }

    try {
      setLoading(true);
      
      const saleData = {
        asset_id: asset.id,
        ...formData,
        sale_price: parseFloat(formData.sale_price)
      };

      const response = await salesAPI.create(saleData);
      
      alert(`✅ Unit berhasil dijual!\n💰 Profit: Rp ${profit.toLocaleString('id-ID')}\n📊 Margin: ${profitMargin}%`);
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to sell asset:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      alert('❌ Gagal menjual unit:\n' + (typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg));
    } finally {
      setLoading(false);
    }
  };

  if (!asset) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="glass-card rounded-xl p-6 w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-foreground">💰 Jual Unit</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Asset Info */}
        <div className="mb-6 p-4 bg-blue-500/10 border-2 border-blue-500/30 rounded-lg">
          <h3 className="text-lg font-semibold text-foreground mb-3">📦 Detail Unit</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground">Brand & Model:</span>
              <p className="font-semibold text-foreground">{asset.brand} {asset.model}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Serial Number:</span>
              <p className="font-semibold text-foreground">{asset.serial_number}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Harga Beli:</span>
              <p className="font-semibold text-foreground">Rp {(asset.purchase_price || 0).toLocaleString('id-ID')}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Nilai Sekarang:</span>
              <p className="font-semibold text-foreground">Rp {(asset.current_value || 0).toLocaleString('id-ID')}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Buyer Information */}
          <div className="p-4 bg-green-500/10 border-2 border-green-500/30 rounded-lg">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <User className="w-5 h-5 mr-2" />
              Data Pembeli
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Nama Pembeli *
                </label>
                <input
                  type="text"
                  name="buyer_name"
                  value={formData.buyer_name}
                  onChange={handleInputChange}
                  placeholder="Nama lengkap pembeli"
                  className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2 flex items-center">
                  <Phone className="w-4 h-4 mr-1" />
                  No. HP *
                </label>
                <input
                  type="tel"
                  name="buyer_phone"
                  value={formData.buyer_phone}
                  onChange={handleInputChange}
                  placeholder="08123456789"
                  className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2 flex items-center">
                  <Mail className="w-4 h-4 mr-1" />
                  Email (opsional)
                </label>
                <input
                  type="email"
                  name="buyer_email"
                  value={formData.buyer_email}
                  onChange={handleInputChange}
                  placeholder="email@example.com"
                  className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  No. KTP (opsional)
                </label>
                <input
                  type="text"
                  name="buyer_id_number"
                  value={formData.buyer_id_number}
                  onChange={handleInputChange}
                  placeholder="3174..."
                  className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Alamat (opsional)
                </label>
                <input
                  type="text"
                  name="buyer_address"
                  value={formData.buyer_address}
                  onChange={handleInputChange}
                  placeholder="Alamat pembeli"
                  className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                />
              </div>
            </div>
          </div>

          {/* Sale Details */}
          <div className="p-4 bg-yellow-500/10 border-2 border-yellow-500/30 rounded-lg">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <DollarSign className="w-5 h-5 mr-2" />
              Detail Penjualan
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Harga Jual *
                </label>
                <input
                  type="number"
                  name="sale_price"
                  value={formData.sale_price}
                  onChange={handleInputChange}
                  placeholder="0"
                  className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground text-xl font-semibold"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2 flex items-center">
                  <CreditCard className="w-4 h-4 mr-1" />
                  Metode Pembayaran
                </label>
                <select
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                >
                  <option value="cash">Cash/Tunai</option>
                  <option value="transfer">Transfer Bank</option>
                  <option value="installment">Cicilan</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Status Pembayaran
                </label>
                <select
                  name="payment_status"
                  value={formData.payment_status}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                >
                  <option value="paid">Lunas</option>
                  <option value="pending">Pending</option>
                  <option value="installment">Cicilan</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2 flex items-center">
                  <FileText className="w-4 h-4 mr-1" />
                  Catatan (opsional)
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="2"
                  placeholder="Catatan tambahan..."
                  className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                />
              </div>
            </div>
          </div>

          {/* Profit Calculation */}
          <div className="p-4 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-lg border-2 border-primary/30">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Harga Beli</p>
                <p className="text-lg font-semibold text-foreground">
                  Rp {(asset.purchase_price || 0).toLocaleString('id-ID')}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Harga Jual</p>
                <p className="text-lg font-semibold text-primary">
                  Rp {(parseFloat(formData.sale_price) || 0).toLocaleString('id-ID')}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Profit</p>
                <p className={`text-xl font-bold ${profit >= 0 ? 'text-secondary' : 'text-destructive'}`}>
                  {profit >= 0 ? '+' : ''}Rp {profit.toLocaleString('id-ID')}
                </p>
                <p className="text-xs text-muted-foreground">({profitMargin}%)</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t-2 border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
              disabled={loading}
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={loading || !formData.buyer_name || !formData.buyer_phone}
              className="px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-lg font-semibold"
            >
              {loading ? (
                <>
                  <span className="animate-spin">⏳</span>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <DollarSign className="w-5 h-5" />
                  <span>Jual Unit</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SellAssetModal;
